import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, Row, Col, Spinner, Alert } from 'react-bootstrap';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

// Components
import { SessionRescheduleModal } from '../../../../components/common/Modal/SesssionRescheduleModal';
import { SessionCancelModal } from '../../../../components/common/Modal/SessionCancelModal';
import { SessionDetailHeader } from './SessionDetailHeader';
import { SessionDetailInfo } from './SessionDetailInfo';
import { SessionDetailRating } from './SessionDetailRating';
import { MessageStudentModal } from '../../profile/MessageStudentModal';

// Services & Utilities
import {
  useGetSessionDetailsQuery,
  useAssignTutorProficiencyMutation,
} from '../../../../../store/services/student/SessionService';
import { 
    useCancelSessionMutation,  
    useRescheduleSessionMutation
} from '../../../../../store/services/tutor/SessionService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../components/PluginsMenu/Toastr/Toastr';

// Constants
const TIME_UPDATE_INTERVAL = 60000; // 1 minute

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// Custom hook for session time management
const useSessionTime = (scheduledTime, sessionDuration) => {
  const [currentDateTime, setCurrentDateTime] = useState(dayjs());
  const sessionDateTime = useMemo(
    () => (scheduledTime ? dayjs(scheduledTime) : null),
    [scheduledTime]
  );
  const sessionEndDateTime = useMemo(
    () => (sessionDateTime && sessionDuration ? sessionDateTime.add(sessionDuration, 'minute') : null),
    [sessionDateTime, sessionDuration]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDateTime(dayjs());
    }, TIME_UPDATE_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  return {
    currentDateTime,
    sessionDateTime,
    sessionEndDateTime,
  };
};

// Loading Component
const LoadingState = () => (
  <div className="container-fluid py-5">
    <div className="text-center py-5">
      <Spinner animation="border" role="status" className="me-2" />
      Loading session details...
    </div>
  </div>
);

// Error Component
const ErrorState = ({ message = 'Error loading session details. Please try again.' }) => (
  <div className="container-fluid py-5">
    <Alert variant="danger">{message}</Alert>
  </div>
);

// Session Actions Component
const SessionActions = ({ 
  sessionStatus, 
  onReschedule, 
  onCancel, 
  onMessageStudent, 
  isProcessing, 
  tutorId 
}) => {
  // Use API session status for determining actions
  const shouldShowActions = sessionStatus === 'Scheduled' || sessionStatus === 'Rescheduled';
  
  if (!shouldShowActions) return null;

  return (
    <Row className="mt-5">
      <Col>
        <div className="d-flex flex-column flex-md-row justify-content-start">
          <Button
            variant="warning"
            className="btn-md me-md-3 mb-3 mb-md-0"
            onClick={onReschedule}
            disabled={isProcessing}
          >
            Reschedule Session
          </Button>
          <Button
            variant="danger"
            className="btn-md me-md-3 mb-3 mb-md-0"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel Session
          </Button>
          <Button 
            variant="outline-primary" 
            className="btn-md"
            onClick={onMessageStudent} 
            disabled={!tutorId}
          >
            <i className="fas fa-envelope me-2"></i>
            Message Student
          </Button>
        </div>
      </Col>
    </Row>
  );
};

export const TutorSessionDetails = () => {
  const { sessionId } = useParams();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const tutorId = currentUser?.tutorId;

  // State
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [rating, setRating] = useState(0);
  const [isRatingSubmitted, setIsRatingSubmitted] = useState(false);

  // API Queries & Mutations
  const { 
    data: sessionData, 
    isLoading, 
    error, 
    refetch 
  } = useGetSessionDetailsQuery({
    slotSessionId: sessionId
  });

  const [cancelSession] = useCancelSessionMutation();
  const [rescheduleSession] = useRescheduleSessionMutation();
  const [assignTutorProficiency] = useAssignTutorProficiencyMutation();

  // Derived state
  const sessionDetails = sessionData?.data;
  const { currentDateTime, sessionDateTime, sessionEndDateTime } = useSessionTime(
    sessionDetails?.scheduledTime,
    sessionDetails?.sessionDuration
  );

  // Use API session status instead of calculating locally
  const sessionStatus = sessionDetails?.sessionStatus || 'Loading...';

  // Memoized values
  const isSessionInFuture = useMemo(
    () => sessionDateTime ? sessionDateTime.isAfter(currentDateTime) : false,
    [sessionDateTime, currentDateTime]
  );

  const timeUntilSession = useMemo(
    () => (isSessionInFuture && sessionDateTime ? sessionDateTime.from(currentDateTime, true) : null),
    [isSessionInFuture, sessionDateTime, currentDateTime]
  );

  const formattedSessionDate = useMemo(
    () => sessionDateTime ? sessionDateTime.format('MMM D, YYYY - h:mm A') : 'Loading...',
    [sessionDateTime]
  );

  const sessionDuration = useMemo(
    () => sessionDetails?.sessionDuration ? `${sessionDetails.sessionDuration} minutes` : 'Loading...',
    [sessionDetails?.sessionDuration]
  );

  // Event Handlers
  const handleMessageStudent = useCallback(() => {
    if (!tutorId) {
      alert('Please login to send a message');
      return;
    }
    setShowMessageModal(true);
  }, [tutorId]);

  const handleCloseMessageModal = useCallback(() => {
    setShowMessageModal(false);
  }, []);

  const handleSendMessage = useCallback(async (message) => {
    if (!tutorId || !sessionDetails?.studentId) return;
    
    setIsSending(true);
    try {
      showSuccessToast(`Message sent successfully to ${sessionDetails.studentName}!`);
    } catch (error) {
      console.error('Error sending message:', error);
      showErrorToast('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  }, [tutorId, sessionDetails?.studentId, sessionDetails?.studentName]);

  const handleSubmitRating = useCallback(async () => {
    if (rating === 0) {
      showErrorToast('Please select a rating before submitting.');
      return;
    }

    try {
      const response = await assignTutorProficiency({
        bookingId: sessionId,
        tutorProficiency: rating
      }).unwrap();
      
      if (response.succeeded) {
        showSuccessToast(response.data || 'Rating submitted successfully!');
        setIsRatingSubmitted(true);
      } else {
        throw new Error(response.errors?.join(', ') || 'Rating submission failed');
      }
    } catch (error) {
      showErrorToast(error.data?.errors?.join(', ') || 'Failed to submit rating. Please try again.');
    }
  }, [rating, sessionId, assignTutorProficiency]);

  const handleReschedule = useCallback(() => {
    setModalType('reschedule');
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setModalType('cancel');
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (!isProcessing) {
      setShowModal(false);
      setModalType('');
    }
  }, [isProcessing]);

  const handleRescheduleSuccess = useCallback(async (startDateTime, duration) => {
    setIsProcessing(true);
    
    try {
      const reschedulePayload = {
        sessionBookingId: sessionId,
        newDate: startDateTime.format('YYYY-MM-DD'),
        startTime: startDateTime.format('HH:mm'),
        endTime: startDateTime.add(duration, 'minute').format('HH:mm')
      };

      const response = await rescheduleSession(reschedulePayload).unwrap();

      if (response.succeeded) {
        showSuccessToast(response.data || 'Session rescheduled successfully!');
        setShowModal(false);
        refetch();
      } else {
        throw new Error(response.errors?.join(', ') || 'Reschedule failed');
      }
    } catch (error) {
      console.error('Error rescheduling session:', error);
      showErrorToast(error.data?.errors?.join(', ') || 'Failed to reschedule session. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [sessionId, rescheduleSession, refetch]);

  const handleCancelSuccess = useCallback(async () => {
    setIsProcessing(true);
    
    try {
      const response = await cancelSession({ SessionBookingId: sessionId }).unwrap();

      if (response.succeeded) {
        showSuccessToast(response.data || 'Session cancelled successfully!');
        setShowModal(false);
        refetch();
      } else {
        throw new Error(response.errors?.join(', ') || 'Cancellation failed');
      }
    } catch (error) {
      console.error('Error cancelling session:', error);
      showErrorToast(error.data?.errors?.join(', ') || 'Failed to cancel session. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [sessionId, cancelSession, refetch]);

  // Early returns for loading and error states
  if (isLoading) return <LoadingState />;
  
  if (error) return <ErrorState />;
  
  if (!sessionDetails) return <ErrorState message="Session not found." />;

  return (
    <>
      <div className="container-fluid py-5 pb-0">
        <Card className="shadow-lg rounded-lg">
          <Card.Body>
            {/* Session Header */}
            <SessionDetailHeader
              sessionStatus={sessionStatus}
              timeUntilSession={timeUntilSession}
              isSessionInFuture={isSessionInFuture}
              studentName={sessionDetails.studentName}
              tutorName={sessionDetails.tutorName}
            />

            {/* Session Info */}
            <SessionDetailInfo 
              sessionDateTime={sessionDetails.scheduledTime}
              sessionDuration={sessionDuration}
              studentName={sessionDetails.studentName}
              tutorName={sessionDetails.tutorName}
              studentImage={sessionDetails.studentImage}
              tutorImage={sessionDetails.tutorImage}
            />

            {/* Language Proficiency Rating */}
            {sessionStatus === 'Completed' && (
              <SessionDetailRating
                rating={rating}
                setRating={setRating}
                isRatingSubmitted={isRatingSubmitted}
                onSubmitRating={handleSubmitRating}
              />
            )}

            <Row className="g-4 mt-4">
              <Col xs={12} md={3}>
                <h6 className="text-muted mb-2">Student Notes</h6>
                <p className="text-muted">
                  {sessionDetails.studentSessionNotes || 'No session notes available.'}
                </p>
              </Col>
              <Col xs={12} md={3}>
                <h6 className="text-muted mb-2">Student Proficiency</h6>
                <p className="badge bg-primary">
                  {sessionDetails.proficiency || 'No proficiency available.'}
                </p>
              </Col>
              <Col xs={12} md={6}>
                <h6 className="text-muted mb-2">{`${(sessionStatus === 'Scheduled' || sessionStatus === 'Rescheduled' || sessionStatus === 'Cancelled') ?  "Meeting Link" : "Recording Link"}`}</h6>
                {sessionDetails.recordingLink ? 
                  <Link to={sessionDetails.recordingLink} target="_blank">
                    {sessionDetails.recordingLink}
                  </Link> :
                  'No link available'
                }
              </Col>
            </Row>

            {/* Action Buttons */}
            <SessionActions
              sessionStatus={sessionStatus}
              onReschedule={handleReschedule}
              onCancel={handleCancel}
              onMessageStudent={handleMessageStudent}
              isProcessing={isProcessing}
              tutorId={tutorId}
            />
          </Card.Body>
        </Card>
      </div>

      {/* Modals */}
      {modalType === 'reschedule' && (
        <SessionRescheduleModal
          show={showModal}
          handleClose={handleCloseModal}
          onSuccess={handleRescheduleSuccess}
          initialDate={sessionDateTime?.format('YYYY-MM-DD')}
          initialStartTime={sessionDateTime?.format('HH:mm')}
          initialEndTime={sessionEndDateTime?.format('HH:mm')}
          isProcessing={isProcessing}
        />
      )}

      {modalType === 'cancel' && (
        <SessionCancelModal
          show={showModal}
          handleClose={handleCloseModal}
          onSuccess={handleCancelSuccess}
          isProcessing={isProcessing}
        />
      )}

      <MessageStudentModal
        show={showMessageModal}
        onClose={handleCloseMessageModal}
        onSendMessage={handleSendMessage}
        tutorId={tutorId}
        studentId={sessionDetails.studentId}
        studentName={sessionDetails.studentName}
        isSending={isSending}
      />
    </>
  );
};